import SpeakButton from "../EnglishTool/SpeakButton";
import { getReadingPool } from "../EnglishTool/pool/readingPool";
import { getSentencePool } from "../EnglishTool/pool/sentencePool";

export const chapterPages = {
    1: [
      {
        title: <h2>Noun Clauses & Appositive Structures in Literary Critique</h2>,
        body: (
          <>
            <p>
              <b>Noun Clauses in Critical Discourse:</b> Advanced essays synthesize multiple viewpoints and employ noun clauses to frame theories, counterclaims, and interpretive stances.<br />
              <b>Subject Example:</b> <i><b>That narrative perspective can destabilize reader expectations</b> is central to postmodern fiction.</i>
              <SpeakButton text="That narrative perspective can destabilize reader expectations is central to postmodern fiction." /><br />
              <b>Object Example:</b> <i>We must assess <b>whether the author intentionally subverts genre conventions</b>.</i>
              <SpeakButton text="We must assess whether the author intentionally subverts genre conventions." />
            </p>
            <p>
              <b>Nominalization for Stylistic Distance:</b> Transform actions into concepts, enhancing formal tone and abstraction.<br />
              <i>"The characters change rapidly." → "This volatility of characterization complicates interpretation."</i>
              <SpeakButton text="This volatility of characterization complicates interpretation." />
              <br />
              <b>Challenge:</b> Turn "The poet speaks passionately." into a nominalized, analytical statement.
            </p>
            <p>
              <b>Appositive Phrases for Nuanced Evidence:</b> Use embedded appositives to clarify theories, critics, or historical context.<br />
              <i>Her thesis, <b>a response to Bakhtin’s dialogism</b>, challenges monologic readings.</i>
              <SpeakButton text="Her thesis, a response to Bakhtin’s dialogism, challenges monologic readings." />
              <br />
              <b>Try it:</b> Spot the appositive: "Orwell’s essay, <b>a scathing satire of propaganda</b>, remains relevant."
            </p>
            <p>
              <b>Intertextual Reading:</b>
              <br />
              <i>{getReadingPool(11)[0]?.statement}</i>
              <br />
              <b>Task:</b> How do the grammatical structures shape the argument or reveal critical bias?
            </p>
          </>
        ),
      },
      {
        title: <h2>Theme, Motif & Symbolism Across Genres</h2>,
        body: (
          <>
            <p>
              <b>Theme in Context:</b> Identify how contemporary issues (identity, justice, globalization) emerge as themes in multiple genres.<br />
              <b>Motif Analysis:</b> Trace motifs—such as water, mirrors, or exile—across novels, poems, or media, exploring their shifting meanings.<br />
              <b>Example:</b> <i>The motif of "exile" links immigrant memoir and dystopian fiction, revealing fractured belonging.</i>
              <SpeakButton text="The motif of exile links immigrant memoir and dystopian fiction, revealing fractured belonging." />
            </p>
            <p>
              <b>Symbolism:</b> Analyze how symbols shift between historical and modern texts.<br />
              <i>
                "The road" in classic poetry may symbolize fate, while in contemporary film it might embody choice or uncertainty.
              </i>
              <SpeakButton text="The road in classic poetry may symbolize fate, while in contemporary film it might embody choice or uncertainty." />
            </p>
            <p>
              <b>Challenge:</b> Identify a motif in a recent film or novel and compare its function to a canonical text.
            </p>
            <p>
              <b>Critical Task:</b> Write a paragraph analyzing how symbolism functions in two different works.
            </p>
          </>
        ),
      },
      {
        title: <h2>Practice: Critical Synthesis & Textual Analysis</h2>,
        body: (
          <>
            <p>
              <b>Task 1:</b> Identify and explain the use of noun clauses, nominalizations, and appositives in:<br />
              "That the characters resist easy classification, a point stressed by recent theorists, reflects the novel’s ambiguity."
              <SpeakButton text="That the characters resist easy classification, a point stressed by recent theorists, reflects the novel’s ambiguity." />
            </p>
            <p>
              <b>Task 2:</b> Compose a thesis statement for a research essay comparing two texts, using at least one noun clause and a complex appositive.
            </p>
            <p>
              <b>Challenge:</b> Write a critical claim that employs symbolism or motif, demonstrating synthesis across genres.
            </p>
          </>
        ),
      },
    ],
  
    2: [
      {
        title: <h2>Voice, Modality & Rhetorical Positioning in Literary Criticism</h2>,
        body: (
          <>
            <p>
              <b>Voice & Academic Authority:</b> Advanced criticism uses both active and passive voice for rhetorical strategy—passive for objectivity, active for stance.<br />
              <i>"The concept was redefined by postcolonial theorists." (passive for distance)</i>
              <SpeakButton text="The concept was redefined by postcolonial theorists." />
              <i>"Fanon exposes the violence of colonial language." (active for emphasis)</i>
              <SpeakButton text="Fanon exposes the violence of colonial language." />
            </p>
            <p>
              <b>Modal Verbs for Nuance:</b> Use "might," "would," "could," etc., to express uncertainty, hypothesis, or suggest alternative readings.<br />
              <i>"The narrative might destabilize the reader’s expectations."</i>
              <SpeakButton text="The narrative might destabilize the reader’s expectations." />
              <b>Try it:</b> Rewrite "The critic proves the interpretation" to express academic uncertainty.
            </p>
            <p>
              <b>Subjunctive & Counterfactuals:</b> Practice forming sophisticated counterarguments.<br />
              <i>"Were the context different, the protagonist’s actions might appear justified."</i>
              <SpeakButton text="Were the context different, the protagonist’s actions might appear justified." />
            </p>
          </>
        ),
      },
      {
        title: <h2>Complex Sentence Structure in Literary and Cultural Analysis</h2>,
        body: (
          <>
            <p>
              <b>Layered Sentences for Complexity:</b> Use nested, compound-complex sentences to reflect sophisticated thinking.<br />
              <i>
                "Although the memoir adopts a linear chronology, it frequently disrupts temporal sequence, and the reader is compelled to reconstruct meaning."
              </i>
              <SpeakButton text="Although the memoir adopts a linear chronology, it frequently disrupts temporal sequence, and the reader is compelled to reconstruct meaning." />
            </p>
            <p>
              <b>Editing for Clarity:</b> Identify and revise run-ons or fragments that obscure critical points.<br />
              <i>Run-on: "The theory is compelling the evidence remains inconclusive."</i>
              <SpeakButton text="The theory is compelling the evidence remains inconclusive." />
              <i>Fragment: "Because the protagonist refuses conformity."</i>
              <SpeakButton text="Because the protagonist refuses conformity." />
            </p>
            <p>
              <b>Challenge:</b> Write a multi-clause sentence analyzing irony or ambiguity in a contemporary text.
            </p>
          </>
        ),
      },
      {
        title: <h2>Practice: Argumentation & Synthesis</h2>,
        body: (
          <>
            <p>
              <b>Task 1:</b> Identify all verbs (including modals and passives):<br />
              <i>{getSentencePool(11).find(s => s.focus === "verbs")?.sentence}</i>
            </p>
            <p>
              <b>Task 2:</b> Compose a critical sentence on identity, agency, or power, using a modal and at least one subordinate clause.
            </p>
            <p>
              <b>Challenge:</b> Transform a summary into an analytical claim by revising verb structure.
            </p>
          </>
        ),
      },
    ],
  
    3: [
      {
        title: <h2>Advanced Adjectives: Theoretical & Evaluative Language</h2>,
        body: (
          <>
            <p>
              <b>Theoretical Adjectives:</b> Apply adjectives from theory: "hegemonic," "liminal," "dialectical," etc.<br />
              <i>"The <b>hegemonic</b> discourse marginalizes dissenting voices."</i>
              <SpeakButton text="The hegemonic discourse marginalizes dissenting voices." />
            </p>
            <p>
              <b>Evaluative Adjectives:</b> Critique methods, interpretations, or themes: "problematic," "insightful," "innovative," "reductive."<br />
              <i>"This reading offers an <b>innovative</b> interpretation, yet remains <b>problematic</b> in its scope."</i>
              <SpeakButton text="This reading offers an innovative interpretation, yet remains problematic in its scope." />
            </p>
            <p>
              <b>Challenge:</b> Use at least two theoretical adjectives in a sentence critiquing a social issue or text.
            </p>
          </>
        ),
      },
      {
        title: <h2>Comparative, Superlative & Multidisciplinary Critique</h2>,
        body: (
          <>
            <p>
              <b>Comparative Evaluation:</b> Contrast scholarly arguments or interpretations.<br />
              <i>"More nuanced than Smith’s analysis, Jones’s reading integrates sociolinguistic evidence."</i>
              <SpeakButton text="More nuanced than Smith’s analysis, Jones’s reading integrates sociolinguistic evidence." />
            </p>
            <p>
              <b>Superlative Judgments:</b> Justify why a perspective is "most persuasive," "least convincing," etc., with textual evidence.<br />
              <i>"Of the recent critiques, this is the most comprehensive."</i>
              <SpeakButton text="Of the recent critiques, this is the most comprehensive." />
            </p>
            <p>
              <b>Challenge:</b> Synthesize two or more perspectives, using comparative and superlative adjectives, plus a theoretical term.
            </p>
          </>
        ),
      },
      {
        title: <h2>Practice: Analytical Writing & Critical Vocabulary</h2>,
        body: (
          <>
            <p>
              <b>Task:</b> Identify and explain at least three theoretical/evaluative adjectives in a provided passage.
            </p>
            <p>
              <b>Compose:</b> Write a paragraph comparing two interpretations of a poem, using evaluative language, theoretical adjectives, and one metaphor.
            </p>
          </>
        ),
      },
    ],
  
    4: [
      {
        title: <h2>Adverbs, Transitions & Discourse Markers in Critique</h2>,
        body: (
          <>
            <p>
              <b>Adverbial Nuance:</b> Use advanced adverbs to hedge, intensify, or shift perspective: "ostensibly," "paradoxically," "increasingly," "fundamentally."<br />
              <i>"Paradoxically, the narrative clarifies as its structure becomes more experimental."</i>
              <SpeakButton text="Paradoxically, the narrative clarifies as its structure becomes more experimental." />
            </p>
            <p>
              <b>Transitions for Discourse:</b> Link ideas with sophistication: "nevertheless," "conversely," "similarly," "on the contrary."<br />
              <i>"Conversely, one could interpret the character’s silence as resistance."</i>
              <SpeakButton text="Conversely, one could interpret the character’s silence as resistance." />
            </p>
            <p>
              <b>Media Critique:</b> Analyze how adverbial markers construct stance or critique in opinion/editorial writing.<br />
              <i>"Arguably, the film offers a subversive commentary on tradition."</i>
              <SpeakButton text="Arguably, the film offers a subversive commentary on tradition." />
            </p>
          </>
        ),
      },
      {
        title: <h2>Critical Adverbial Mastery & Discourse Analysis</h2>,
        body: (
          <>
            <p>
              <b>Find and Explain:</b> Identify adverbs and discourse markers in a provided academic or media excerpt, explaining their rhetorical effect.
            </p>
            <p>
              <b>Compose:</b> Write a critical argument on a social theme, using at least three advanced adverbs, two transition markers, and one theoretical term.
            </p>
          </>
        ),
      },
      {
        title: <h2>Practice: Advanced Synthesis & Argumentation</h2>,
        body: (
          <>
            <p>
              <b>Compose:</b> An analytical paragraph on a literary, cultural, or media topic, using advanced adverbs, theoretical language, and comparative logic.
            </p>
            <p>
              <b>Challenge:</b> How do adverbs and transitions shape argument, bias, or critical perspective in a chosen article?
            </p>
          </>
        ),
      },
    ]
  };